import React from 'react'

import {
  SubmitFormRequestElementResult,
  SubmitFormResponse,
} from '@cash-web/protos/squareup/franklin/app/submit_form.pb'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { Blockers } from '@cash-web/protos/squareup/franklin/blockers.pb'
import {
  BlockerDescriptor,
  BlockerDescriptorTheme,
  BlockersSupplement,
  ScenarioPlan,
} from '@cash-web/protos/squareup/franklin/scenarios.pb'

export type BlockerActions = Omit<BlockerAction, 'text' | 'localized_text'>

export type BlockerActionKey = keyof BlockerActions

export type BlockerActionData<Action extends BlockerActionKey> = { key: Action; action: BlockerActions[Action] }

export type BlockerActionDataDetail = { [key in BlockerActionKey]: BlockerActionData<key> }[BlockerActionKey]

export type ExecuteBlockerAction = (
  action: BlockerAction,
  results?: SubmitFormRequestElementResult[]
) => Promise<void | SubmitFormResponse>

export type FlowElementEntry = {
  trackingName: string
  element: React.ReactElement
}

export type FlowBlockers = Blockers

export interface FlowBlockerDescriptor extends BlockerDescriptor {
  blocker?: FlowBlockers
}

type NonNullable<T> = Exclude<T, null | undefined>

// url isn't a blocker, so we remove it from the data
// TODO: what should we do with url?
export type BlockersData = Omit<FlowBlockers, 'url'>

export type BlockerType = keyof BlockersData

export type BlockerData<Type extends BlockerType> = NonNullable<BlockersData[Type]>

export type BlockerSupplement<Type extends BlockerType> = BlockersSupplement[Type extends keyof BlockersSupplement
  ? Type
  : never]

export type BlockerComponentProps<Type extends BlockerType> = {
  id?: string
  blocker: BlockerData<Type>
  supplement?: BlockerSupplement<Type>
  skippable?: boolean
  theme?: BlockerDescriptorTheme
}

type BlockerDataDetailGeneric<T extends BlockerType> = BlockerComponentProps<T> & {
  type: T
  trackingName: string
  remoteSkip: boolean
  groupId?: string
}

export type BlockerDataDetail = { [key in BlockerType]: BlockerDataDetailGeneric<key> }[BlockerType]

export const getBlockerType = (blocker?: FlowBlockers) => {
  return Object.keys(blocker || {}).find(key => key !== 'url') as BlockerType
}

export const getBlockerDataDetail = (blockerDescriptor: FlowBlockerDescriptor): BlockerDataDetail => {
  const {
    id,
    blocker: blockers,
    remote_skip,
    skippable,
    supplement: supplements,
    theme,
    group_id: groupId,
  } = blockerDescriptor

  const type = getBlockerType(blockers)

  const trackingName = type

  const blocker = blockers?.[type] as BlockerData<typeof type>

  const supplement = supplements?.[type as keyof typeof supplements] as BlockerSupplement<typeof type>

  return {
    id,
    type,
    trackingName,
    blocker,
    supplement,
    skippable,
    remoteSkip: remote_skip === true,
    theme,
    groupId,
  } as BlockerDataDetail
}
export const isTerminalBlocker = (scenarioPlan?: ScenarioPlan) => {
  return !scenarioPlan?.blocker_descriptors?.length || scenarioPlan.blocker_descriptors[0].id === 'FLOW_TERMINATION'
}
